import { useMutation } from '@tanstack/react-query'
import { useId, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { submitEnquiry, type EnquiryInput } from '../api/enquiries'
import { SPECIALTIES } from '../api/specialists'
import { Button } from '../components/Button'
import { CheckboxField, Field } from '../components/Field'
import { Footer } from '../components/Footer'
import { Icon } from '../components/Icon'
import { Navbar } from '../components/Navbar'
import { ApiError } from '../lib/api'

const MOBILITY: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'independent', label: 'Independent' },
  { value: 'walker', label: 'Walker / cane' },
  { value: 'wheelchair', label: 'Wheelchair' },
  { value: 'bed_bound', label: 'Bed-bound' },
]

type FormState = {
  full_name: string
  email: string
  country_of_origin: string
  phone: string
  whatsapp: string
  specialty_slug: string
  preferred_dates_text: string
  condition_description: string
  mobility: string
  consent: boolean
}

const EMPTY: FormState = {
  full_name: '',
  email: '',
  country_of_origin: '',
  phone: '',
  whatsapp: '',
  specialty_slug: '',
  preferred_dates_text: '',
  condition_description: '',
  mobility: '',
  consent: false,
}

/**
 * Tourism enquiry form (M7-T05) at `/tourism/enquiry`. Captures an
 * international patient's lead and POSTs to `/api/v1/enquiries` (M7-T01) — the
 * one Phase-1 write. Consent is required; no payment is collected. A
 * `?hospital=` param (from the profile) preselects the hospital, and a
 * `?condition=` param (from the home page's concierge intake) prefills the
 * condition box.
 *
 * M17-T13 — restyled onto the M16 atoms. The hand-rolled inputs, labels and
 * submit button are now `Field`, `CheckboxField` and `Button`, so this form
 * gets the ported geometry (radius 16, 21/17 padding, 16/24 labels) and the one
 * focus ring the system specifies.
 *
 * NO PAYMENT FIELDS, and no room for any: Phase 1 collects nothing, and card
 * and MoMo details never touch our own forms (ADR-0017). Validation ink is
 * `--bed-bad`, never `--sos` — they resolve to the same red today and mean
 * different things.
 */
export function TourismEnquiry(): ReactNode {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const preferredHospital = params.get('hospital')

  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    condition_description: params.get('condition') ?? '',
  }))
  const set = <K extends keyof FormState>(k: K, v: FormState[K]): void =>
    setForm((f) => ({ ...f, [k]: v }))

  const mutation = useMutation({
    mutationFn: (input: EnquiryInput) => submitEnquiry(input),
  })

  const canSubmit =
    form.full_name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.country_of_origin.trim().length === 2 &&
    form.consent &&
    !mutation.isPending

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!canSubmit) return
    const input: EnquiryInput = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      country_of_origin: form.country_of_origin.trim(),
      consent_data_handling: form.consent,
    }
    if (form.phone.trim()) input.phone = form.phone.trim()
    if (form.whatsapp.trim()) input.whatsapp = form.whatsapp.trim()
    if (form.specialty_slug) input.specialty_slug = form.specialty_slug
    if (preferredHospital) input.preferred_hospital_slug = preferredHospital
    if (form.preferred_dates_text.trim())
      input.preferred_dates_text = form.preferred_dates_text.trim()
    if (form.condition_description.trim())
      input.condition_description = form.condition_description.trim()
    if (form.mobility) input.mobility = form.mobility
    mutation.mutate(input)
  }

  return (
    <main className="flex min-h-screen flex-col bg-bg-app font-sans text-ink-1">
      {/* M17-T04 — shared chrome. */}
      <Navbar tagline="International patients" />

      <div className="mx-auto w-full max-w-xl px-5 py-8 sm:px-8">
        {mutation.isSuccess ? (
          <Confirmation
            reference={mutation.data.reference}
            onDone={() => navigate('/')}
          />
        ) : (
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
            <div>
              <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1 sm:text-d-sm">
                Tell us what you need
              </h1>
              <p className="mt-2 text-[16px] leading-6 text-ink-2">
                Your enquiry goes to the AMTC concierge only — encrypted in
                transit. No payment is taken here.
              </p>
            </div>

            {preferredHospital && (
              <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-bg px-3 py-1 text-[14px] font-medium text-accent-ink">
                <Icon name="badge" size={14} /> Enquiring about{' '}
                {preferredHospital}
              </p>
            )}

            {mutation.isError && (
              <p
                role="alert"
                className="rounded-r3 border border-bed-bad bg-bed-bad-bg px-4 py-3 text-[14px] leading-5 text-bed-bad"
              >
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Something went wrong. Please try again.'}
              </p>
            )}

            <Field
              label="Full name *"
              required
              value={form.full_name}
              onChange={(e) => set('full_name', e.target.value)}
              autoComplete="name"
            />
            <Field
              label="Email *"
              type="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              autoComplete="email"
            />
            {/* Shell rather than `Field`: the value is displayed uppercase but
                submitted as typed (the API takes either), and `Field` styles
                its wrapper, not its input. */}
            <Shell label="Country of origin *" hint="ISO code, e.g. NG, GB, US">
              {(id, describedBy) => (
                <input
                  id={id}
                  type="text"
                  required
                  aria-describedby={describedBy}
                  value={form.country_of_origin}
                  onChange={(e) => set('country_of_origin', e.target.value)}
                  maxLength={2}
                  className={`${controlCls} uppercase`}
                />
              )}
            </Shell>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                autoComplete="tel"
              />
              <Field
                label="WhatsApp"
                type="tel"
                value={form.whatsapp}
                onChange={(e) => set('whatsapp', e.target.value)}
              />
            </div>

            {/* `Field` wraps an <input> only, deliberately — see its note. The
                select, textarea and chip group below reuse its shell classes
                rather than growing it a `as` prop that would make it all
                branches. */}
            <Shell label="Specialty">
              {(id) => (
                <select
                  id={id}
                  value={form.specialty_slug}
                  onChange={(e) => set('specialty_slug', e.target.value)}
                  className={controlCls}
                >
                  <option value="">— Select —</option>
                  {SPECIALTIES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </Shell>

            <Shell label="Brief description of your condition">
              {(id) => (
                <textarea
                  id={id}
                  rows={4}
                  value={form.condition_description}
                  onChange={(e) => set('condition_description', e.target.value)}
                  placeholder="Plain language is fine."
                  className={`${controlCls} h-auto resize-y`}
                />
              )}
            </Shell>

            <fieldset className="flex flex-col gap-2 border-0 p-0">
              <legend className="text-[16px] font-medium leading-6 text-ink-1">
                Mobility
              </legend>
              <div className="flex flex-wrap gap-2">
                {MOBILITY.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    aria-pressed={form.mobility === m.value}
                    onClick={() =>
                      set('mobility', form.mobility === m.value ? '' : m.value)
                    }
                    className={
                      form.mobility === m.value
                        ? 'rounded-full border border-accent bg-accent px-4 py-2 text-[14px] font-medium text-accent-on'
                        : 'rounded-full border border-line-2 bg-bg-app px-4 py-2 text-[14px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <Field
              label="Preferred dates"
              value={form.preferred_dates_text}
              onChange={(e) => set('preferred_dates_text', e.target.value)}
              placeholder="e.g. Late September"
            />

            <CheckboxField
              checked={form.consent}
              onChange={(v) => set('consent', v)}
              label={
                <>
                  I consent to my information being shared with the AMTC
                  concierge team to handle this enquiry.{' '}
                  <span className="text-bed-bad">*</span>
                </>
              }
            />

            <Button
              type="submit"
              full
              disabled={!canSubmit}
              loading={mutation.isPending}
            >
              Send enquiry to concierge
            </Button>
          </form>
        )}
      </div>

      <Footer />
    </main>
  )
}

/** The `Field` input shell, for the controls `Field` itself does not wrap. */
const controlCls =
  'w-full min-w-0 rounded-r3 border border-line-1 bg-bg-app px-[21px] py-[17px] text-[16px] leading-6 text-ink-1 outline-none placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

function Shell({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  /** Receives the input id and, when there is a hint, the id to describe it. */
  children: (id: string, describedBy: string | undefined) => ReactNode
}): ReactNode {
  const reactId = useId()
  const id = `field-${reactId}`
  const msgId = `${id}-msg`

  return (
    <div className="flex w-full flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[16px] font-medium leading-6 text-ink-1"
      >
        {label}
      </label>
      {children(id, hint ? msgId : undefined)}
      {hint ? (
        <span id={msgId} className="text-[14px] leading-5 text-ink-3">
          {hint}
        </span>
      ) : null}
    </div>
  )
}

function Confirmation({
  reference,
  onDone,
}: {
  reference: string
  onDone: () => void
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <span
        className="grid h-14 w-14 place-items-center rounded-full bg-bed-good-bg text-bed-good"
        aria-hidden="true"
      >
        <Icon name="check" size={28} />
      </span>
      <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
        Enquiry sent
      </h1>
      <p className="max-w-sm text-[16px] leading-6 text-ink-2">
        The AMTC concierge will reply within one business day. Keep your
        reference for any follow-up.
      </p>
      <span className="rounded-r3 border border-line-2 bg-bg-app px-4 py-2 font-mono text-[14px] font-semibold tracking-wide text-ink-1">
        {reference}
      </span>
      <Button kind="secondary" onClick={onDone}>
        Back to tourism home
      </Button>
    </div>
  )
}
